import { useState } from "react";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import api from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { SERVICES } from "@/data/services";

const initial = { name: "", email: "", phone: "", service: "", postcode: "", message: "" };

const QuoteForm = ({ variant = "card", title, subtitle }) => {
  const [data, setData] = useState(initial);
  const [submitting, setSubmitting] = useState(false);
  const set = (k) => (e) => setData((d) => ({ ...d, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!data.name || !data.email || !data.phone || !data.service) {
      toast.error("Please fill in your name, email, phone and service.");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/quote", data);
      toast.success("Thanks — your quote request is in. We’ll call you back within one working day.");
      setData(initial);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Something went wrong. Please call us directly.");
    } finally {
      setSubmitting(false);
    }
  };

  const isInverse = variant === "inverse";

  return (
    <form
      onSubmit={submit}
      data-testid="quote-form"
      className={
        isInverse
          ? "rounded-lg bg-white p-7 md:p-9 border border-border shadow-sm"
          : "rounded-lg bg-white p-7 md:p-9 border border-border"
      }
    >
      {title && <h3 className="font-display text-2xl md:text-3xl font-semibold text-crest-navy">{title}</h3>}
      {subtitle && <p className="mt-2 text-sm text-muted-foreground font-body">{subtitle}</p>}
      <div className="mt-6 grid gap-4 md:grid-cols-2">
        <div>
          <label className="text-xs uppercase tracking-[0.14em] text-crest-navy/70 font-medium">Full name</label>
          <Input value={data.name} onChange={set("name")} placeholder="Jane Smith" data-testid="quote-name" className="mt-2 h-11" required />
        </div>
        <div>
          <label className="text-xs uppercase tracking-[0.14em] text-crest-navy/70 font-medium">Phone</label>
          <Input value={data.phone} onChange={set("phone")} placeholder="07XXX XXXXXX" data-testid="quote-phone" className="mt-2 h-11" required />
        </div>
        <div>
          <label className="text-xs uppercase tracking-[0.14em] text-crest-navy/70 font-medium">Email</label>
          <Input type="email" value={data.email} onChange={set("email")} placeholder="you@email.com" data-testid="quote-email" className="mt-2 h-11" required />
        </div>
        <div>
          <label className="text-xs uppercase tracking-[0.14em] text-crest-navy/70 font-medium">Postcode</label>
          <Input value={data.postcode} onChange={set("postcode")} placeholder="L1 8JQ" data-testid="quote-postcode" className="mt-2 h-11" />
        </div>
        <div className="md:col-span-2">
          <label className="text-xs uppercase tracking-[0.14em] text-crest-navy/70 font-medium">Service required</label>
          <select
            value={data.service}
            onChange={set("service")}
            data-testid="quote-service"
            required
            className="mt-2 h-11 w-full rounded-md border border-input bg-transparent px-3 text-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
          >
            <option value="">Select a service…</option>
            {SERVICES.map((s) => (
              <option key={s.slug} value={s.name}>{s.name}</option>
            ))}
            <option value="Other">Something else</option>
          </select>
        </div>
        <div className="md:col-span-2">
          <label className="text-xs uppercase tracking-[0.14em] text-crest-navy/70 font-medium">Project details</label>
          <Textarea value={data.message} onChange={set("message")} rows={4}
            placeholder="Tell us about your project — size, scope, timeline…"
            data-testid="quote-message" className="mt-2" />
        </div>
      </div>
      <button
        type="submit"
        disabled={submitting}
        data-testid="quote-submit"
        className="mt-6 inline-flex items-center justify-center gap-2 rounded-md bg-crest-green px-7 py-3.5 text-sm font-semibold text-white hover:bg-crest-green-700 transition-colors duration-200 disabled:opacity-60 w-full md:w-auto"
      >
        {submitting && <Loader2 className="h-4 w-4 animate-spin" />}
        Request my free estimate
      </button>
      <p className="mt-3 text-xs text-muted-foreground font-body">
        We’ll respond within one working day · No obligation · Liverpool, North West, North Wales &amp; Midlands
      </p>
    </form>
  );
};

export default QuoteForm;
